/**
 * Network Map — geographic view of NEM assets, substations and transmission
 * lines. Real geo from aus_warehouse (GA + OpenNEM). Default shows every
 * layer; the user can search or click a feature to open the right-hand data
 * panel. For energy assets we also pull the last 30 days of generation.
 *
 * Many DUIDs share one site coordinate (e.g. BW01-04 → Bayswater). Clusters
 * spiderfy on click, and the panel additionally lists co-located units as
 * chips so every unit is always reachable even when pins overlap exactly.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Chip, Autocomplete, TextField, IconButton, Skeleton, Alert,
  Table, TableBody, TableCell, TableHead, TableRow, Tooltip,
} from '@mui/material';
import { cachedGet } from '../../utils/cachedFetch';
import { createFilterOptions } from '@mui/material/Autocomplete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import {
  MapContainer, TileLayer, Marker, Polyline, useMap,
} from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { API_BASE_URL, LineChartBox } from './_shared';
import { StatCard, formatMWh } from '../charts/shared';

const FUEL_COLOR = {
  'Black Coal': '#374151', 'Brown Coal': '#78350f', Gas: '#ef4444',
  Other: '#6b7280', Hydro: '#06b6d4', Wind: '#3b82f6', Solar: '#f59e0b',
};
const fuelColor = (f) => FUEL_COLOR[f] || '#6b7280';
const SUBSTATION_COLOR = '#0EA5A4';
const SELECT_COLOR = '#D98A2B';

// Transmission line styling by voltage class. The selected line is drawn
// thick + brand-amber + opaque (and re-drawn on top) so it stands out.
function lineStyle(kv, selected) {
  if (selected) return { color: SELECT_COLOR, weight: 5, opacity: 1 };
  if (kv >= 330) return { color: '#D98A2B', weight: 1.8, opacity: 0.7 };
  if (kv >= 220) return { color: '#64748b', weight: 1.3, opacity: 0.55 };
  return { color: '#94A3B8', weight: 0.9, opacity: 0.4 };
}

// Asset = round pin with a lightning bolt, fuel-coloured.
function assetIcon(color) {
  const s = 24;
  return L.divIcon({
    className: 'lm-ic',
    html: `<svg width="${s}" height="${s}" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="9.5" fill="${color}" stroke="#fff" stroke-width="2"/>
      <path d="M13 5.5l-5.5 8H11l-1 5 5.5-8H12z" fill="#fff"/></svg>`,
    iconSize: [s, s], iconAnchor: [s / 2, s / 2],
  });
}

// Substation = diamond (rotated square) with an inner bar — a clearly
// different silhouette from the round asset pin.
function subIcon() {
  const s = 18;
  return L.divIcon({
    className: 'lm-ic',
    html: `<svg width="${s}" height="${s}" viewBox="0 0 24 24">
      <rect x="4.5" y="4.5" width="15" height="15" rx="2.5"
        transform="rotate(45 12 12)" fill="${SUBSTATION_COLOR}"
        stroke="#fff" stroke-width="2"/>
      <rect x="9.5" y="11" width="5" height="2" fill="#fff"/></svg>`,
    iconSize: [s, s], iconAnchor: [s / 2, s / 2],
  });
}

// A non-interactive amber ring drawn over the selected point so the
// active asset/substation reads clearly without re-clustering markers.
function ringIcon() {
  const s = 38;
  return L.divIcon({
    className: 'lm-ring',
    html: `<div style="width:${s}px;height:${s}px;border-radius:50%;
      border:3px solid ${SELECT_COLOR};box-sizing:border-box;
      box-shadow:0 0 0 2px rgba(255,255,255,.9)"></div>`,
    iconSize: [s, s], iconAnchor: [s / 2, s / 2],
  });
}

const AU_CENTER = [-28.5, 134.0];
const coordKey = (lat, lon) => `${lat},${lon}`;
// Stable unique key per search option (names collide across ~5k features).
const optKey = (o) => `${o.kind}:${o.ref.asset_id ?? o.ref.id}`;
// Cap rendered options — the index is ~5k items and MUI is unvirtualised.
const filterSearch = createFilterOptions({ limit: 40 });

function MapBridge({ onReady }) {
  const map = useMap();
  useEffect(() => { onReady(map); }, [map, onReady]);
  return null;
}

function Row({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.6 }}>
      <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary', textAlign: 'right' }}>
        {String(value)}
      </Typography>
    </Box>
  );
}

function AssetGeneration({ duid }) {
  const [st, setSt] = useState({ loading: true, error: null, data: null });
  useEffect(() => {
    let alive = true;
    setSt({ loading: true, error: null, data: null });
    cachedGet(`${API_BASE_URL}/api/market/asset_generation`,
      { params: { duid, days: 30 } })
      .then((r) => { if (alive) setSt({ loading: false, error: null, data: r.data }); })
      .catch((e) => {
        if (alive) setSt({ loading: false, data: null,
          error: e.response?.data?.error || e.message || 'Failed to load generation' });
      });
    return () => { alive = false; };
  }, [duid]);

  if (st.loading) return <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2, mt: 1 }} />;
  if (st.error) return <Alert severity="warning" sx={{ mt: 1 }}>{st.error}</Alert>;
  const d = st.data || {};
  const series = d.series || [];
  if (!series.length) {
    return (
      <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mt: 1 }}>
        No dispatch generation in the last 30 days (asset may be a load,
        battery, or non-scheduled).
      </Typography>
    );
  }
  const k = d.kpis || {};
  const x = series.map((s) => {
    const dt = new Date(`${s.date}T00:00:00`);
    return dt.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
  });
  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        <StatCard label="Total · 30d"
          value={k.total_mwh == null ? '—' : `${(k.total_mwh / 1000).toFixed(1)} GWh`} />
        <StatCard label="Peak" value={k.peak_mw == null ? '—' : `${k.peak_mw} MW`} />
        <StatCard label="Capacity factor"
          value={k.capacity_factor_pct == null ? '—' : `${k.capacity_factor_pct}%`}
          color={SELECT_COLOR} />
      </Box>
      <LineChartBox
        x={x}
        series={[{
          data: series.map((s) => s.mwh), label: 'Generation (MWh/day)',
          color: SELECT_COLOR, showMark: false, curve: 'linear', connectNulls: true,
        }]}
        height={220}
        yFormat={formatMWh}
      />
    </Box>
  );
}

// AEMO detail for a selected GA transmission line: linkage summary +
// thermal ratings + active/recent outages. Fetched on click of any line on
// the map; lines without a reliable crosswalk show a short "not linked" note.
function LineAemoDetail({ gaObjectId }) {
  const [st, setSt] = useState({ loading: true, error: null, data: null });
  useEffect(() => {
    let alive = true;
    setSt({ loading: true, error: null, data: null });
    cachedGet(`${API_BASE_URL}/api/market/transmission_line_detail`,
      { params: { ga_objectid: gaObjectId } })
      .then((r) => { if (alive) setSt({ loading: false, error: null, data: r.data }); })
      .catch((e) => {
        if (alive) setSt({ loading: false, data: null,
          error: e.response?.data?.error || e.message || 'Failed to load line detail' });
      });
    return () => { alive = false; };
  }, [gaObjectId]);

  if (st.loading) return <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2, mt: 2 }} />;
  if (st.error) return <Alert severity="warning" sx={{ mt: 2 }}>{st.error}</Alert>;
  const d = st.data || {};
  if (!d.linked) {
    return (
      <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 2, fontStyle: 'italic' }}>
        {d.reason || 'No AEMO data linked.'}
      </Typography>
    );
  }
  const s = d.summary || {};
  const ratings = d.static_ratings || [];
  const outages = d.outages || [];
  const k = d.kpis || {};
  return (
    <>
      <Box sx={{ mt: 2, p: 1.5, borderRadius: 1.5, bgcolor: 'rgba(217,138,43,0.06)',
        border: '1px solid', borderColor: 'rgba(217,138,43,0.25)' }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.05em',
          textTransform: 'uppercase', color: 'primary.main' }}>
          Linked to AEMO element
        </Typography>
        <Typography sx={{ fontSize: '0.82rem', color: 'text.primary', mt: 0.25 }}>
          {s.aemo_description}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75, flexWrap: 'wrap' }}>
          <Chip size="small" label={`#${s.aemo_element_id}`}
            sx={{ fontSize: '0.66rem', height: 20 }} />
          <Chip size="small" label={s.match_confidence}
            sx={{ fontSize: '0.66rem', height: 20,
              bgcolor: s.match_confidence === 'exact' ? 'rgba(14,165,164,0.15)' : 'rgba(217,138,43,0.15)' }} />
          {(s.aemo_substations || '').split(',').filter(Boolean).map((sub) => (
            <Chip key={sub} size="small" label={sub} variant="outlined"
              sx={{ fontSize: '0.66rem', height: 20 }} />
          ))}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mt: 2, mb: 1 }}>
        <Box>
          <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary',
            textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>
            Active outages
          </Typography>
          <Typography sx={{ fontSize: '1.2rem', fontWeight: 700,
            color: k.active_outages ? 'primary.main' : 'text.primary' }}>
            {k.active_outages ?? 0}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary',
            textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>
            Recent (90d)
          </Typography>
          <Typography sx={{ fontSize: '1.2rem', fontWeight: 700, color: 'text.primary' }}>
            {k.recent_outages ?? 0}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary',
            textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>
            Rating rows
          </Typography>
          <Typography sx={{ fontSize: '1.2rem', fontWeight: 700, color: 'text.primary' }}>
            {k.static_ratings ?? 0}
          </Typography>
        </Box>
      </Box>

      {ratings.length > 0 && (
        <>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em',
            textTransform: 'uppercase', color: 'text.secondary', mt: 2, mb: 0.5 }}>
            Static thermal capacity (MW)
          </Typography>
          <Table size="small" sx={{ '& td, & th': { fontSize: '0.74rem', p: '4px 6px' } }}>
            <TableHead>
              <TableRow>
                {['Substation', 'Level', 'Application', 'MW'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, color: 'text.secondary' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {ratings.slice(0, 12).map((r, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ fontWeight: 600 }}>{r.substation}</TableCell>
                  <TableCell>{r.level}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{r.application}</TableCell>
                  <TableCell sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {r.value_mw}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {ratings.length > 12 && (
            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', mt: 0.5 }}>
              +{ratings.length - 12} more rating rows
            </Typography>
          )}
        </>
      )}

      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em',
        textTransform: 'uppercase', color: 'text.secondary', mt: 2, mb: 0.5 }}>
        Outages · active + last 90 days
      </Typography>
      {outages.length === 0 ? (
        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
          No active or recent outages.
        </Typography>
      ) : (
        <Table size="small" sx={{ '& td, & th': { fontSize: '0.72rem', p: '4px 6px' } }}>
          <TableHead>
            <TableRow>
              {['Sub', 'Start', 'End', 'Status', 'Reason'].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 700, color: 'text.secondary' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {outages.slice(0, 15).map((o) => {
              const fmtD = (iso) => iso
                ? new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' })
                : '—';
              return (
                <TableRow key={o.outage_id}>
                  <TableCell sx={{ fontWeight: 600 }}>{o.substation || '—'}</TableCell>
                  <TableCell>{fmtD(o.start)}</TableCell>
                  <TableCell>{fmtD(o.end)}</TableCell>
                  <TableCell>{o.status || '—'}</TableCell>
                  <Tooltip title={o.reason || ''} arrow placement="left">
                    <TableCell sx={{ color: 'text.secondary',
                      maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap' }}>
                      {o.reason || '—'}
                    </TableCell>
                  </Tooltip>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </>
  );
}

function DetailPanel({ sel, onClose, onPickSibling }) {
  if (!sel) return null;
  const { kind, data: f, group } = sel;
  const siblings = kind === 'asset' && group && group.length > 1 ? group : null;
  return (
    <Box sx={{
      width: { xs: '100%', sm: 360 }, flexShrink: 0,
      borderLeft: '1px solid', borderColor: 'divider',
      bgcolor: 'background.paper', display: 'flex', flexDirection: 'column',
      height: '100%',
    }}>
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        p: 2, borderBottom: '1px solid', borderColor: 'divider', gap: 1,
      }}>
        <Box>
          <Typography sx={{
            fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em',
            textTransform: 'uppercase', color: 'primary.main',
          }}>
            {kind === 'asset' ? 'Energy Asset'
              : kind === 'substation' ? 'Substation' : 'Transmission Line'}
          </Typography>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.2, mt: 0.25 }}>
            {f.name || f.asset_name || '—'}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
        {siblings && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{
              fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em',
              textTransform: 'uppercase', color: 'text.secondary', mb: 0.75,
            }}>
              {siblings.length} units at this site
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {siblings.map((u) => (
                <Chip
                  key={u.asset_id}
                  label={u.asset_id}
                  size="small"
                  onClick={() => onPickSibling(u)}
                  variant={u.asset_id === f.asset_id ? 'filled' : 'outlined'}
                  sx={{
                    fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer',
                    bgcolor: u.asset_id === f.asset_id ? 'rgba(217,138,43,0.16)' : 'transparent',
                    color: u.asset_id === f.asset_id ? 'primary.main' : 'text.secondary',
                    borderColor: 'divider',
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {kind === 'asset' && (
          <>
            <Row label="DUID" value={f.asset_id} />
            <Row label="Fuel" value={f.fuel} />
            <Row label="Region" value={f.region} />
            <Row label="Registered capacity" value={f.capacity != null ? `${f.capacity} MW` : null} />
            <Row label="Site" value={f.site} />
            <Row label="Owner" value={f.owner} />
            <Row label="Geo source" value={f.geo_source === 'opennem' ? 'OpenNEM' : 'GA power stations'} />
            {f.needs_review && (
              <Chip size="small" label="Location needs review" color="warning"
                variant="outlined" sx={{ mt: 1, fontSize: '0.68rem' }} />
            )}
            <Typography sx={{
              fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em',
              textTransform: 'uppercase', color: 'text.secondary', mt: 3, mb: 0.5,
            }}>
              Generation · last 30 days
            </Typography>
            <AssetGeneration duid={f.asset_id} />
          </>
        )}
        {kind === 'substation' && (
          <>
            <Row label="Voltage" value={f.voltage_kv != null ? `${f.voltage_kv} kV` : null} />
            <Row label="Status" value={f.status} />
            <Row label="State" value={f.state} />
            <Row label="Locality" value={f.locality} />
            <Row label="Coordinates" value={`${f.lat}, ${f.lon}`} />
          </>
        )}
        {kind === 'line' && (
          <>
            <Row label="Voltage" value={f.kv != null ? `${f.kv} kV` : null} />
            <Row label="Status" value={f.status} />
            <Row label="State" value={f.state} />
            <Row label="Length" value={f.length_m != null ? `${(f.length_m / 1000).toFixed(1)} km` : null} />
            <LineAemoDetail gaObjectId={f.id} />
          </>
        )}
      </Box>
    </Box>
  );
}

export default function MarketNetworkMap() {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [layers, setLayers] = useState({ assets: true, substations: true, lines: true });
  const [sel, setSel] = useState(null);

  useEffect(() => {
    let alive = true;
    cachedGet(`${API_BASE_URL}/api/market/map`)
      .then((r) => { if (alive) setState({ loading: false, error: null, data: r.data }); })
      .catch((e) => {
        if (alive) setState({ loading: false, data: null,
          error: e.response?.data?.error || e.message || 'Failed to load map data' });
      });
    return () => { alive = false; };
  }, []);

  const d = state.data;

  // Group assets that share an exact coordinate (multiple DUIDs per site).
  const siteGroups = useMemo(() => {
    const m = new Map();
    (d?.assets || []).forEach((a) => {
      const key = coordKey(a.lat, a.lon);
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(a);
    });
    return m;
  }, [d]);

  const selectAsset = (a) => {
    setSel({ kind: 'asset', data: a, group: siteGroups.get(coordKey(a.lat, a.lon)) });
  };

  // Cluster children memoised on data+layers only, so selecting a feature
  // (which re-renders this component) does NOT trigger a full re-cluster.
  const assetMarkers = useMemo(() => (
    (d?.assets || []).map((a) => (
      <Marker
        key={`a${a.asset_id}`} position={[a.lat, a.lon]}
        icon={assetIcon(fuelColor(a.fuel))}
        eventHandlers={{ click: () => selectAsset(a) }}
      />
    ))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [d]);

  const subMarkers = useMemo(() => (
    (d?.substations || []).map((s) => (
      <Marker
        key={`s${s.id}`} position={[s.lat, s.lon]}
        icon={subIcon()}
        eventHandlers={{ click: () => setSel({ kind: 'substation', data: s }) }}
      />
    ))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [d]);

  const searchOpts = useMemo(() => {
    if (!d) return [];
    const o = [];
    (d.assets || []).forEach((a) => o.push({
      label: a.name || a.asset_id, group: 'Assets', kind: 'asset', ref: a,
      at: [a.lat, a.lon],
    }));
    (d.substations || []).forEach((s) => o.push({
      label: s.name || `Substation ${s.id}`, group: 'Substations',
      kind: 'substation', ref: s, at: [s.lat, s.lon],
    }));
    (d.lines || []).forEach((ln) => o.push({
      label: ln.name || `Line ${ln.id}`, group: 'Transmission lines',
      kind: 'line', ref: ln, at: ln.paths?.[0]?.[0],
    }));
    return o;
  }, [d]);

  const focus = (kind, ref, at) => {
    if (kind === 'asset') selectAsset(ref);
    else setSel({ kind, data: ref });
    const map = mapRef.current;
    if (!map) return;
    if (kind === 'line' && ref.paths?.length) {
      map.fitBounds(L.latLngBounds(ref.paths.flat()), { padding: [60, 60], maxZoom: 11 });
    } else if (at && at[0] != null) {
      map.flyTo(at, 12, { duration: 0.6 });
    }
  };

  const selLine = sel?.kind === 'line' ? sel.data : null;
  const selPoint = sel && (sel.kind === 'asset' || sel.kind === 'substation')
    ? [sel.data.lat, sel.data.lon] : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100dvh - 64px)' }}>
      <Box sx={{ px: { xs: 1.5, sm: 2.5 }, pt: 1.5, pb: 1 }}>
        <Box
          onClick={() => navigate('/market')} role="button" tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/market'); }}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 1,
            cursor: 'pointer', color: 'text.secondary',
            '&:hover': { color: 'primary.main' },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Back to Market</Typography>
        </Box>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 1.5,
        }}>
          <Box>
            <Typography sx={{
              fontSize: '1.5rem', fontWeight: 700, color: 'text.primary',
              letterSpacing: '-0.025em', lineHeight: 1.1,
            }}>
              Network Map
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mt: 0.25 }}>
              NEM assets, substations & transmission lines — real geo (GA + OpenNEM)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Autocomplete
              size="small"
              options={searchOpts}
              groupBy={(o) => o.group}
              getOptionLabel={(o) => o.label}
              filterOptions={filterSearch}
              sx={{ width: { xs: 220, sm: 320 } }}
              onChange={(_, v) => v && focus(v.kind, v.ref, v.at)}
              renderOption={(props, o) => {
                const { key, ...rest } = props;
                return <li {...rest} key={optKey(o)}>{o.label}</li>;
              }}
              renderInput={(p) => <TextField {...p} placeholder="Search assets, substations, lines…" />}
            />
            {[
              ['assets', `Assets${d ? ` (${d.counts.assets})` : ''}`],
              ['substations', `Substations${d ? ` (${d.counts.substations})` : ''}`],
              ['lines', `Lines${d ? ` (${d.counts.lines})` : ''}`],
            ].map(([k, lbl]) => (
              <Chip
                key={k} label={lbl} size="small"
                onClick={() => setLayers((s) => ({ ...s, [k]: !s[k] }))}
                variant={layers[k] ? 'filled' : 'outlined'}
                sx={{
                  fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                  bgcolor: layers[k] ? 'rgba(217,138,43,0.14)' : 'transparent',
                  borderColor: 'divider',
                  color: layers[k] ? 'primary.main' : 'text.secondary',
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {state.error && <Alert severity="error" sx={{ mx: 2.5, mb: 1 }}>{state.error}</Alert>}

      <Box sx={{ flex: 1, display: 'flex', minHeight: 0,
        borderTop: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
          {state.loading && (
            <Skeleton variant="rectangular" sx={{ position: 'absolute', inset: 0, zIndex: 500 }} />
          )}
          <MapContainer
            center={AU_CENTER} zoom={5} minZoom={4}
            style={{ height: '100%', width: '100%' }}
            preferCanvas
          >
            <MapBridge onReady={(m) => { mapRef.current = m; }} />
            <TileLayer
              attribution='&copy; OpenStreetMap &copy; CARTO'
              url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
              subdomains="abcd"
            />

            {d && layers.lines && d.lines.map((ln) => (
              <Polyline
                key={`l${ln.id}`}
                positions={ln.paths}
                pathOptions={lineStyle(ln.kv || 0, false)}
                eventHandlers={{ click: () => focus('line', ln, ln.paths?.[0]?.[0]) }}
              />
            ))}
            {/* Selected line re-drawn last (on top) with the highlight style. */}
            {selLine && (
              <Polyline
                key={`sel-l${selLine.id}`}
                positions={selLine.paths}
                pathOptions={lineStyle(selLine.kv || 0, true)}
                interactive={false}
              />
            )}

            {d && layers.substations && (
              <MarkerClusterGroup
                chunkedLoading
                maxClusterRadius={45}
                spiderfyOnMaxZoom
                showCoverageOnHover={false}
                spiderfyDistanceMultiplier={1.6}
              >
                {subMarkers}
              </MarkerClusterGroup>
            )}

            {d && layers.assets && (
              <MarkerClusterGroup
                chunkedLoading
                maxClusterRadius={50}
                spiderfyOnMaxZoom
                showCoverageOnHover={false}
                spiderfyDistanceMultiplier={1.8}
              >
                {assetMarkers}
              </MarkerClusterGroup>
            )}

            {/* Non-interactive amber ring over the selected point. */}
            {selPoint && selPoint[0] != null && (
              <Marker
                position={selPoint}
                icon={ringIcon()}
                interactive={false}
                zIndexOffset={1000}
              />
            )}
          </MapContainer>
        </Box>

        {sel && (
          <DetailPanel
            sel={sel}
            onClose={() => setSel(null)}
            onPickSibling={(u) => setSel((s) => ({ ...s, data: u }))}
          />
        )}
      </Box>
    </Box>
  );
}
